import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  Cloud,
  Shield,
  Brain,
  Settings,
  Code2,
  Database,
  ArrowRight,
  CheckCircle2,
} from 'lucide-react';
import './Services.css';

const services = [
  {
    id: 'cloud-devops',
    icon: <Cloud size={28} />,
    title: 'Cloud & DevOps',
    subtitle: 'Scalable Infrastructure',
    description:
      'Modernize your infrastructure with our secure cloud-native architectures. Whether you need AWS cloud migration, Azure managed services, or DevOps containerization, we ensure 99.99% uptime and limitless scalability for your enterprise.',
    offerings: [
      'Cloud Migration & Modernisation',
      'Kubernetes Orchestration',
      'CI/CD Pipeline Automation',
      'Infrastructure as Code (Terraform)',
      'Multi-cloud Strategy (AWS / Azure / GCP)',
    ],
    technologies: ['AWS', 'Azure', 'GCP', 'Kubernetes', 'Terraform', 'Docker', 'Jenkins', 'ArgoCD'],
    accentColor: '#06b6d4',
    caseStudy: {
      client: 'FinTech Enterprise',
      metric: '70% Cost Reduction',
      desc: 'Migrated monolithic banking platform to Kubernetes, cutting infra costs and improving deployment frequency from monthly to daily.',
    },
  },
  {
    id: 'cybersecurity',
    icon: <Shield size={28} />,
    title: 'Cybersecurity',
    subtitle: 'Zero-Trust Security',
    description:
      'Protect your enterprise from evolving digital threats. Our comprehensive cybersecurity services include penetration testing, zero-trust architecture implementation, and continuous threat monitoring to ensure total data compliance (ISO 27001, GDPR).',
    offerings: [
      '24/7 SOC-as-a-Service',
      'Penetration Testing & Red Team',
      'SIEM Implementation & Tuning',
      'Compliance (ISO 27001, SOC 2, GDPR)',
      'Incident Response & Forensics',
    ],
    technologies: ['Splunk', 'CrowdStrike', 'Palo Alto', 'Tenable', 'Okta', 'Sentinel', 'MITRE ATT&CK'],
    accentColor: '#ef4444',
    caseStudy: {
      client: 'Healthcare Group',
      metric: '0 Breaches in 3 Years',
      desc: 'Implemented zero-trust architecture and 24/7 SOC for a 12-hospital network, achieving full HIPAA compliance and zero security incidents.',
    },
  },
  {
    id: 'ai-automation',
    icon: <Brain size={28} />,
    title: 'AI & Automation',
    subtitle: 'Intelligent Systems',
    description:
      'Turn your data into a competitive advantage. We integrate generative AI, predictive analytics, and intelligent automation into your existing workflows to reduce operational costs and drive unprecedented efficiency for your business.',
    offerings: [
      'Machine Learning Model Development',
      'Robotic Process Automation (RPA)',
      'Natural Language Processing (NLP)',
      'Predictive Analytics Platforms',
      'LLM Integration & Fine-tuning',
    ],
    technologies: ['Python', 'TensorFlow', 'PyTorch', 'OpenAI API', 'LangChain', 'UiPath', 'Hugging Face', 'Databricks'],
    accentColor: '#7c3aed',
    caseStudy: {
      client: 'Logistics Leader',
      metric: '40% Ops Cost Saved',
      desc: 'Deployed RPA bots and an ML-powered demand forecasting model, automating 15,000+ daily transactions and reducing operational spend by 40%.',
    },
  },
  {
    id: 'custom-software',
    icon: <Code2 size={28} />,
    title: 'Custom Software',
    subtitle: 'Full-Stack Development',
    description:
      'We don\'t just write code; we build resilient digital ecosystems. Our custom software development team engineers high-performance web applications, scalable enterprise platforms, and secure APIs tailored to your complex business requirements.',
    offerings: [
      'Web Application Development (React)',
      'Mobile Apps (Flutter / React Native)',
      'Microservices & API Architecture',
      'Node.js Backend Engineering',
      'Third-party & Legacy Integration',
    ],
    technologies: ['React', 'Node.js', 'Flutter', 'TypeScript', 'GraphQL', 'PostgreSQL', 'Redis', 'Kafka'],
    accentColor: '#2563eb',
    caseStudy: {
      client: 'E-commerce Scale-up',
      metric: '3× Revenue Growth',
      desc: 'Re-platformed legacy e-commerce site to a React/Node.js microservices stack, cutting page load by 60% and tripling revenue within 6 months.',
    },
  },
  {
    id: 'managed-services',
    icon: <Settings size={28} />,
    title: 'IT Managed Services',
    subtitle: '24/7 Operations',
    description:
      'Let your team focus on innovation while we run the IT estate. Our managed services practice delivers round-the-clock NOC operations, ITIL-aligned helpdesk support, proactive monitoring, and bulletproof backup strategies — guaranteeing uptime, performance, and peace of mind.',
    offerings: [
      'Network Operations Centre (NOC)',
      'ITIL-Aligned Service Desk',
      'End-User Computing & Helpdesk',
      'Proactive Infrastructure Monitoring',
      'Disaster Recovery & Backup',
    ],
    technologies: ['ServiceNow', 'Zabbix', 'Nagios', 'ManageEngine', 'Veeam', 'Microsoft 365', 'Intune'],
    accentColor: '#f59e0b',
    caseStudy: {
      client: 'Manufacturing Corp',
      metric: '99.98% Uptime SLA',
      desc: 'Took over full IT management for 2,500-seat manufacturer, delivering 99.98% uptime and reducing ticket resolution time from 48 hrs to 4 hrs.',
    },
  },
  {
    id: 'data-analytics',
    icon: <Database size={28} />,
    title: 'Data & Analytics',
    subtitle: 'Business Intelligence',
    description:
      'Turn fragmented data into crystal-clear decisions. We design end-to-end data platforms — from ingestion and governance to self-serve BI dashboards and ML pipelines — empowering every stakeholder with real-time insights and the confidence to act on them.',
    offerings: [
      'BI Dashboard Development (Power BI / Tableau)',
      'ETL Pipeline Engineering',
      'Data Lake & Warehouse Architecture',
      'ML Pipeline Deployment',
      'Data Governance & Quality Frameworks',
    ],
    technologies: ['Power BI', 'Tableau', 'Snowflake', 'dbt', 'Apache Spark', 'Airflow', 'BigQuery', 'Azure Synapse'],
    accentColor: '#10b981',
    caseStudy: {
      client: 'Retail Chain (200+ Stores)',
      metric: '28% Revenue Uplift',
      desc: 'Built a unified data lake and real-time BI platform enabling daily inventory optimisation decisions, driving a 28% revenue uplift in 9 months.',
    },
  },
];

const fadeInUp = {
  hidden: { opacity: 0, y: 32 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.55, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] },
  }),
};

const navItemVariants = {
  hidden: { opacity: 0, x: -16 },
  visible: (i) => ({
    opacity: 1,
    x: 0,
    transition: { duration: 0.4, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] },
  }),
};

const detailVariants = {
  initial: { opacity: 0, x: 20 },
  animate: { opacity: 1, x: 0, transition: { duration: 0.3, ease: [0.22, 1, 0.36, 1] } },
  exit: { opacity: 0, x: -20 },
};

const servicesJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'ItemList',
  name: 'Wayug Infotech Solutions – IT Services',
  itemListElement: services.map((s, idx) => ({
    '@type': 'ListItem',
    position: idx + 1,
    name: s.title,
    description: s.description,
    url: `https://wayug.com/services#${s.id}`,
  })),
};

export default function Services() {
  const [activeService, setActiveService] = useState(0);
  const current = services[activeService];

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(servicesJsonLd)}</script>

      <section id="services" className="services" aria-labelledby="services-heading">
        <div className="container">
          {/* ── Section Header ── */}
          <motion.div
            className="section-header"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-80px' }}
          >
            <motion.span className="section-label" variants={fadeInUp} custom={0}>
              OUR EXPERTISE
            </motion.span>
            <motion.h2 id="services-heading" className="section-heading" variants={fadeInUp} custom={1}>
              Solutions Built for{' '}
              <span className="gradient-text">Enterprise Scale</span>
            </motion.h2>
            <motion.p className="section-subheading" variants={fadeInUp} custom={2}>
              From infrastructure to intelligence — we deliver end-to-end technology
              services that accelerate growth, harden security, and future-proof your
              organisation.
            </motion.p>
          </motion.div>

          {/* ── Services Layout ── */}
          <div className="services-layout">
            {/* LEFT — Navigation */}
            <nav className="services-nav" aria-label="Service categories">
              {services.map((svc, idx) => (
                <motion.button
                  key={svc.id}
                  className={`service-nav-item${activeService === idx ? ' active' : ''}`}
                  onClick={() => setActiveService(idx)}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, margin: '-60px' }}
                  custom={idx}
                  variants={navItemVariants}
                  style={
                    activeService === idx
                      ? { borderLeftColor: svc.accentColor }
                      : {}
                  }
                  aria-current={activeService === idx ? 'true' : undefined}
                >
                  <span
                    className="service-nav-icon"
                    style={{ color: activeService === idx ? svc.accentColor : undefined }}
                  >
                    {React.cloneElement(svc.icon, { size: 20 })}
                  </span>
                  <span className="service-nav-title">{svc.title}</span>
                  {activeService === idx && (
                    <ArrowRight size={14} className="service-nav-arrow" style={{ color: svc.accentColor }} />
                  )}
                </motion.button>
              ))}
            </nav>

            {/* RIGHT — Detail Panel */}
            <motion.div
              key={activeService}
              className="service-detail"
              id={current.id}
              variants={detailVariants}
              initial="initial"
              animate="animate"
              exit="exit"
            >
              {/* Header */}
              <div className="service-detail-header">
                <div
                  className="service-icon-wrap"
                  style={{ background: `${current.accentColor}1a`, border: `1px solid ${current.accentColor}33` }}
                >
                  <span style={{ color: current.accentColor }}>
                    {React.cloneElement(current.icon, { size: 28 })}
                  </span>
                </div>
                <div>
                  <h3 className="service-detail-title">{current.title}</h3>
                  <p className="service-detail-subtitle">{current.subtitle}</p>
                </div>
              </div>

              {/* Description */}
              <p className="service-detail-desc">{current.description}</p>

              {/* Offerings */}
              <div className="service-offerings">
                {current.offerings.map((item) => (
                  <div className="offering-item" key={item}>
                    <CheckCircle2 size={15} style={{ color: current.accentColor, flexShrink: 0, marginTop: '1px' }} />
                    <span>{item}</span>
                  </div>
                ))}
              </div>

              {/* Tech Tags */}
              <div className="service-tech">
                {current.technologies.map((tech) => (
                  <span className="tech-tag" key={tech}>
                    {tech}
                  </span>
                ))}
              </div>

              {/* Case Study */}
              <div
                className="service-case-study"
                style={{ borderColor: `${current.accentColor}22` }}
              >
                <p className="case-study-label" style={{ color: current.accentColor }}>
                  Case Study
                </p>
                <p className="case-study-metric">{current.caseStudy.metric}</p>
                <p className="case-study-client">{current.caseStudy.client}</p>
                <p className="case-study-desc">{current.caseStudy.desc}</p>
              </div>

              {/* CTA */}
              <a 
                href={`https://wa.me/918287999236?text=Hi!%20I%20would%20like%20to%20request%20a%20free%20assessment%20for%20${encodeURIComponent(current.title)}.`}
                target="_blank" 
                rel="noopener noreferrer" 
                className="btn btn-accent"
              >
                Get Free Assessment
                <ArrowRight size={16} />
              </a>
            </motion.div>
          </div>
        </div>
      </section>
    </>
  );
}
